// Streamed website check (`POST /api/tools/check-website` with
// `Accept: application/x-ndjson`). The API sends one JSON line per event:
// `start` (the locations that will answer), one `result` per location in the
// order they answer, then `done` with the same data as the JSON reply. The
// page shows every location at once and fills each row as it answers.
import type { V2WebsiteCheck } from '@/lib/api-v2-client'
import { LOCATIONS } from '@/lib/locations'
import type { WebsiteCheckResult } from '@/lib/website-check'

export type WebsiteCheckEvent =
  | { event: 'start'; url: string; regions: string[]; note?: string }
  | { event: 'result'; result: WebsiteCheckResult }
  | { event: 'done'; data: V2WebsiteCheck }
  | { event: 'error'; message: string }

export type CheckRun = {
  status: 'idle' | 'running' | 'done' | 'error'
  url?: string
  /** Locations that will answer, in the server's order. */
  regions: string[]
  results: Record<string, WebsiteCheckResult>
  note?: string
  error?: string
  /** HTTP status of a failed request (429 = rate limited). */
  errorStatus?: number
}

export const IDLE_RUN: CheckRun = { status: 'idle', regions: [], results: {} }

/** A new run. Until `start` arrives, every location is shown as checking. */
export function startRun(url: string): CheckRun {
  return { status: 'running', url, regions: LOCATIONS.map((l) => l.code), results: {} }
}

/** Splits a text buffer into complete lines and the unfinished rest. Pure. */
export function takeLines(buffer: string): { lines: string[]; rest: string } {
  const parts = buffer.split('\n')
  const rest = parts.pop() ?? ''
  return { lines: parts.filter((l) => l.trim() !== ''), rest }
}

/** The events of a finished JSON reply (older API or a non-streaming proxy). */
export function eventsFromJson(data: V2WebsiteCheck): WebsiteCheckEvent[] {
  return [
    { event: 'start', url: data.url, regions: data.results.map((r) => r.region), note: data.note },
    ...data.results.map((result) => ({ event: 'result' as const, result })),
    { event: 'done', data },
  ]
}

export function applyEvent(run: CheckRun, ev: WebsiteCheckEvent): CheckRun {
  switch (ev.event) {
    case 'start':
      return { ...run, url: ev.url, regions: ev.regions, note: ev.note }
    case 'result':
      return { ...run, results: { ...run.results, [ev.result.region]: ev.result } }
    case 'done': {
      const results = Object.fromEntries(ev.data.results.map((r) => [r.region, r]))
      return {
        ...run,
        status: 'done',
        url: ev.data.url,
        regions: ev.data.results.map((r) => r.region),
        results,
        note: ev.data.note ?? run.note,
      }
    }
    case 'error':
      return { ...run, status: 'error', error: ev.message }
  }
}

const NEVER_ANSWERED = 'This location did not answer'

/** The stream ended without `done`: locations that never answered are unavailable. */
export function finishIncomplete(run: CheckRun): CheckRun {
  const results = { ...run.results }
  for (const region of run.regions) {
    results[region] ??= { region, location: '', status: 'unavailable', error: NEVER_ANSWERED }
  }
  return { ...run, status: 'done', results }
}

/** One row per location in the planned order; `pending` until it answers. */
export function rowsOf(run: CheckRun): WebsiteCheckResult[] {
  return run.regions.map((region) => run.results[region] ?? { region, location: '', status: 'pending' })
}

/** Results that have answered, in the planned order. */
export function answeredResults(run: CheckRun): WebsiteCheckResult[] {
  return run.regions.flatMap((region) => (run.results[region] ? [run.results[region]] : []))
}
